import {applyTransaction, EntityStore, QueryEntity} from "@datorama/akita"
import {StickyEntityState} from "app/Interface/EntityService"
import useAuthStore, {subscribeAuth} from "app/Service/Auth/AuthService"
import produce from "immer"
import Logger from "logger/logger"
import React, {createContext, ReactNode, useCallback, useEffect, useState} from "react"
import API from "api/API"
import {systemModuleQuery} from "store/system/module/system.module.store"

interface IStoreFunctions {
  createStore: (name: string, loadFn?: createStoreLoadFn) => boolean
  getStore: <T>(name: string) => EntityStore<StickyEntityState<T>> | undefined
  getQuery: <T>(name: string) => QueryEntity<StickyEntityState<T>, T> | undefined
  getQueryById: <T>(storeId: string) => QueryEntity<StickyEntityState<T>, T>
  queries: { [key: string]: QueryEntity<any> }
  getLoadFn: (storeName: string) => ((items: any[]) => any) | undefined
}

type createStoreLoadFn = ((rawItems: any[]) => any[]) | undefined

interface IStoreContext {
  [key: string]: EntityStore
}

interface IQueryContext {
  [key: string]: QueryEntity<StickyEntityState<any>, any>
}

interface IStoreLoadFn {
  [key: string]: createStoreLoadFn
}

const emptyStore = new EntityStore<StickyEntityState<any>>({}, {name: "emptyStore"})
const emptyQuery = new QueryEntity<StickyEntityState<any>, any, string>(emptyStore)

export const StoreFunctions = createContext<IStoreFunctions>({
  createStore: () => false,
  getStore: () => emptyStore,
  getQuery: () => emptyQuery,
  getQueryById: () => emptyQuery,
  queries: {},
  getLoadFn: () => () => null,
})

/** @deprecated */
export function StoreService({children}: { children: ReactNode | ReactNode[] }) {

  const {isAuthenticated} = useAuthStore([
    subscribeAuth.isAuthenticated
  ], 'StoreService')

  const [stores, setStores] = useState<IStoreContext>({})
  const [queries, setQueries] = useState<IQueryContext>({})
  const [storeLoadFn, setStoreLoadFn] = useState<IStoreLoadFn>({})

  useEffect(() => {
    Logger('deprecated.Service.Store.init')
    emptyStore.destroy()
    return () => {
      Logger('deprecated.Service.Store.unloading')
    }
  }, [])

  useEffect(() => {
    if (undefined === isAuthenticated) return
    if (!isAuthenticated) {
      Object.values(stores).map(store => {
        store.destroy()
      })
      setStores({})
      setQueries({})
    }
  }, [isAuthenticated])

  function createStore<T>(storeName: string, loadFn: createStoreLoadFn = undefined) {
    if (Object.keys(stores).includes(storeName)) {
      return true
    }

    const sysModule = systemModuleQuery.getAll().find(m => m.name === storeName)
    if (!sysModule)
      throw new Error(`Module does not exist: ${storeName}`)

    const fqn = getStoreName(storeName)

    const initState: StickyEntityState<T> = {
      loading: undefined,
      url: `/${storeName}`,
      id: sysModule.id,
      active: null,
      total: undefined,
      searchQuery: [],
      create: undefined,
      update: undefined
    }

    const store = new EntityStore<StickyEntityState<T>>(initState, {name: fqn, resettable: true, producerFn: produce})
    const query = new QueryEntity<StickyEntityState<T>, T>(store)

    setStores(state => ({
      ...state,
      [storeName]: store
    }))
    setQueries(state => ({
      ...state,
      [storeName]: query
    }))
    setStoreLoadFn(state => ({
      ...state,
      [storeName]: loadFn
    }))

    return true
  }

  useEffect(() => {
    const keys = Object.keys(stores)
    if (keys.length < 1) return

    const newStores = keys.filter(key => stores[key].getValue().loading === undefined)
    if (newStores.length < 1) return

    newStores.map(async key => {
      stores[key]!.setLoading(true)

      const get = await API.get(`/${key}`)
      let entityData: { data: any[], total: number } = {
        data: [],
        total: 0
      }

      const loadFn = storeLoadFn[key]

      if (loadFn) {
        entityData.data = loadFn(get.data.data)
        entityData.total = get.data.total
      } else
        entityData = get.data

      applyTransaction(() => {
        stores[key]!.set(entityData.data)
        stores[key]!.update(state => {
          state.loading = false
          state.total = entityData.total
        })
      })
    })
  }, [stores])

  const getStore = useCallback(function getStore<T>(storeName: string) {
    return stores[storeName] as EntityStore<StickyEntityState<T>>
  }, [stores])

  const getQuery = useCallback(function getQuery(storeName: string) {
    return queries[storeName]
  }, [queries])

  // Used to initialize stores based on connections
  const getQueryById = useCallback(function getQueryById(storeId: string) {
    const query = Object.values(queries).find(q => {
      return q.getValue().id === storeId
    })
    if (!query)
      throw new Error("Query not found")
    return query
  }, [queries])

  const getLoadFn = useCallback((storeName: string) => {
    return storeLoadFn[storeName]
  }, [storeLoadFn])

  return (
    <StoreFunctions.Provider value={{createStore, getStore, getQuery, getQueryById, queries, getLoadFn}}>
      {children}
    </StoreFunctions.Provider>
  )

  function getStoreName(name: string) {
    return 'module.' + name
  }
}
